import axios from 'axios';
import store from '@/store/store';
import AppError from '@/services/error.service';
import UserService from '@/services/user.service';
import { consoleInfo } from '@/utils/console.utils';
import Router from '@/router';

const ApiService = {
  /**
   * Inicializa el Servicio API
   *
   * @param {String} baseURL - Url base del API
   * @param {String} apiVersion - Ruta de la versión del API
   */
  init(baseURL, apiVersion) {
    // Configuración base
    axios.defaults.baseURL = `${baseURL}${apiVersion}`;
    axios.defaults.headers.post['Content-Type'] = 'application/json';
    axios.defaults.headers.post.Accept = 'application/json';
    axios.defaults.headers.post['Access-Control-Allow-Origin'] = '*';
    axios.defaults.timeout = 10000;

    // Interceptor
    /* prettier-ignore */
    axios.interceptors.response.use(
      response => response,
      // eslint-disable-next-line consistent-return
      async error => {
        /*
         * Sin conexión a internet
         * La petición no puede realizarse porque no hay conexión a internet
         */
        if (error.message === 'Network Error') {
          store.dispatch('globalModule/setError', 'networkError');
        }

        /*
         * Tiempo excedido
         * La petición es abortada porque ha tardado más del tiempo definido en la variable 'timeout'
         */
        else if (error.code === 'ECONNABORTED') {
          throw new AppError('timeout');
        }

        /*
         * Backend no responde
         * La petición no puede realizarse porque el backend no nos responde
         */
        else if (!error.response) {
          store.dispatch('globalModule/setError', 'backendDown');
        }

        /*
         * Sesión expirada
         * La sesión del usuario ha expirado, intentamos obtener un nuevo token con el refreshToken,
         * y volvemos realizar la petición.
         */
        else if (error.response.status === 401) {
          consoleInfo('Api Service: Ha expirado la sesión, pruebo con el refreshtoken');
          const token = await UserService.refreshToken();
          error.config.headers.Authorization = `Bearer ${token}`;
          return axios(error.config);
        }

        /*
         * El refresh token ha caducado
         * El refresh token también ha caducado, cierro la sesión
         */
        else if (error.response.data._id === 'tokenNotFound' &&
          !Router.history.current.path.includes('resetPassword')) {
          store.dispatch('globalModule/setError', 'tokenNotFound');
        }

        /*
         * Sesión cerrada
         * La petición no puede realizarse porque el usuario no tiene ninguna sesión abierta,
         */
        else if (error.response.status === 403 &&
          !Router.history.current.path.includes('resetPassword')) {
          store.dispatch('globalModule/setError', 'sessionClosed');
        }

        return Promise.reject(error);
      },
    );
  },

  setBaseURL(baseURL, apiVersion) {
    axios.defaults.baseURL = `${baseURL}${apiVersion}`;
    this.removeHeader();
  },

  setHeader(token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },

  removeHeader() {
    axios.defaults.headers.common = {};
  },

  get(resource, data) {
    if (data) return axios.get(resource, data);
    return axios.get(resource);
  },

  post(resource, data) {
    return axios.post(resource, data);
  },

  put(resource, data) {
    return axios.put(resource, data);
  },

  patch(resource, data) {
    return axios.patch(resource, data);
  },

  delete(resource, data) {
    if (data) return axios.delete(resource, { data });
    return axios.delete(resource, data);
  },
};

export default ApiService;
